import React, { createContext, useCallback, useReducer } from "react";
import { NavigationRoute, Employee } from "../types";
import { Actions, ActionType, setActivePage, setFormLinks, setShowFullMenu, setUserInfo, setUserNavLinks } from "./actions";
import { Announcement, AppLink } from "../types/announcements";

interface IState {
    isAuth: boolean;
    fetchingData: boolean;
    showFullMenu: boolean;
    currentPage: any;
    showAllPayslip: boolean;
    latestYear: number;
    navLinks: NavigationRoute[];
    user?: Employee;
    formLinks?: NavigationRoute[];
    activePage: string;
    announcements: Announcement[] | null;
    selectedAnncmnt?: Announcement;
    appLink?: AppLink;
}

const initialState: IState = {
    isAuth: false,
    fetchingData: false,
    showFullMenu: window.innerWidth > 1200, // MENU open in larger screen
    currentPage: "",
    showAllPayslip: false,
    latestYear: 0,
    navLinks: [],
    activePage: "dashboard",
    announcements: null,
};

interface IAppContext {
    state: IState;
    dispatch: React.Dispatch<Actions>;
    setInit: (dataProps: any) => void;
    toggleFullMenu: () => void;
}

const AppContext = createContext<IAppContext>({
    state: initialState,
    dispatch: () => null,
    setInit: () => {},
    toggleFullMenu: () => {},
});

const { Provider } = AppContext;

const reducer = (state: IState, action: Actions) => {
    switch (action.type) {
        case ActionType.SetFetchingData:
            return {
                ...state,
                fetchingData: action.payload,
            };

        case ActionType.SetShowFullMenu:
            return {
                ...state,
                showFullMenu: action.payload,
            };

        case ActionType.SetCurrentPage:
            return {
                ...state,
                currentPage: action.payload,
            };

        case ActionType.SetUserNavLinks:
            return {
                ...state,
                navLinks: action.payload,
            };

        case ActionType.SetUserInfo:
            return {
                ...state,
                user: action.payload,
            };

        case ActionType.SetUserLinks:
            return {
                ...state,
                formLinks: action.payload,
            };

        case ActionType.SetActivePage:
            return {
                ...state,
                activePage: action.payload,
            };

        case ActionType.SetAnnouncements:
            return {
                ...state,
                announcements: action.payload,
            };

        case ActionType.SetSelectedAnncmnt:
            return {
                ...state,
                selectedAnncmnt: action.payload,
            };

        case ActionType.SetAppLink:
            return {
                ...state,
                appLink: action.payload,
            };

        default:
            return state;
    }
};

const AppProvider = ({ children }: { children: JSX.Element }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const setInit = (dataProps: any) => {
        dispatch(
            setUserInfo({
                employeeNo: dataProps.user.EmployeeNo,
                firstName: dataProps.user.FirstName,
                lastName: dataProps.user.LastName,
                companyNo: dataProps.user.CompanyNo || dataProps.user.CompanyNumber,
            })
        );
        dispatch(setUserNavLinks(dataProps.navLinks));
        dispatch(setFormLinks(dataProps.formLinks));
        dispatch(setActivePage(dataProps.activePage));
    };

    const toggleFullMenu = useCallback(() => {
        dispatch(setShowFullMenu(!state.showFullMenu));
    }, [state.showFullMenu]);

    return (
        <Provider
            value={{
                state,
                dispatch,
                setInit,
                toggleFullMenu,
            }}>
            {children}
        </Provider>
    );
};

export { AppContext, AppProvider };
